
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { formatCPF, formatPhone, formatCEP } from '../../shared/utils/formatters';

interface ClienteCadastro {
  id: number;
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  status: string;
  dataRegistro: string;
}

export default function CadastroClientes() {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nomeCompleto: '',
    cpf: '',
    email: '',
    telefone: '',
    dataNascimento: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    rendaFamiliar: '',
    observacoes: ''
  });

  const clientes: ClienteCadastro[] = [];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsFormVisible(false);
    setFormData({
      nomeCompleto: '',
      cpf: '',
      email: '',
      telefone: '',
      dataNascimento: '',
      endereco: '',
      cidade: '',
      estado: '',
      cep: '',
      rendaFamiliar: '',
      observacoes: ''
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const filteredClientes = clientes.filter(cliente =>
    cliente.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.cpf.includes(searchTerm) ||
    cliente.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Ativo':
        return 'bg-green-100 text-green-800';
      case 'Pendente':
        return 'bg-yellow-100 text-yellow-800';
      case 'Inativo':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[3000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
          <div>
            <h1 className="text-3xl font-fraunces font-bold text-textPrimary mb-2">
              Cadastro de Clientes
            </h1>
            <p className="font-instrument text-textSecondary">
              Gerencie o cadastro de clientes do sistema
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link
              to="/dashboard"
              className="text-primary hover:text-primary/80 font-instrument font-medium cursor-pointer mr-4"
            >
              ← Voltar ao Dashboard
            </Link>
          </div>
        </div>

        {/* Actions Bar */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line text-textSecondary"></i>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                />
              </div>
            </div>
            <button
              onClick={() => setIsFormVisible(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-lg font-instrument font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Novo Cliente
            </button>
          </div>
        </div>

        {/* Form Modal */}
        {isFormVisible && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-screen overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-fraunces font-semibold text-textPrimary">
                    Novo Cliente
                  </h3>
                  <button
                    onClick={() => setIsFormVisible(false)}
                    className="text-textSecondary hover:text-textPrimary cursor-pointer"
                  >
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className="ri-close-line text-xl"></i>
                    </div>
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="nomeCompleto"
                      required
                      value={formData.nomeCompleto}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="Digite o nome completo"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      CPF *
                    </label>
                    <input
                      type="text"
                      name="cpf"
                      required
                      value={formatCPF(formData.cpf)}
                      onChange={(e) => handleChange({...e, target: {...e.target, value: e.target.value.replace(/\D/g, '')}})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="000.000.000-00"
                      maxLength={14}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      E-mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="email@exemplo.com"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Telefone *
                    </label>
                    <input
                      type="text"
                      name="telefone"
                      required
                      value={formatPhone(formData.telefone)}
                      onChange={(e) => handleChange({...e, target: {...e.target, value: e.target.value.replace(/\D/g, '')}})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Data de Nascimento *
                    </label>
                    <input
                      type="date"
                      name="dataNascimento"
                      required
                      value={formData.dataNascimento}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      CEP
                    </label>
                    <input
                      type="text"
                      name="cep"
                      value={formatCEP(formData.cep)}
                      onChange={(e) => handleChange({...e, target: {...e.target, value: e.target.value.replace(/\D/g, '')}})}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="00000-000"
                      maxLength={9}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Endereço *
                    </label>
                    <input
                      type="text"
                      name="endereco"
                      required
                      value={formData.endereco}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="Rua, número, bairro"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Cidade *
                    </label>
                    <input
                      type="text"
                      name="cidade"
                      required
                      value={formData.cidade}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="Nome da cidade"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Estado *
                    </label>
                    <select
                      name="estado"
                      required
                      value={formData.estado}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                    >
                      <option value="">Selecione o estado</option>
                      <option value="SP">São Paulo</option>
                      <option value="RJ">Rio de Janeiro</option>
                      <option value="MG">Minas Gerais</option>
                      <option value="RS">Rio Grande do Sul</option>
                      <option value="PR">Paraná</option>
                      <option value="SC">Santa Catarina</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Renda Familiar
                    </label>
                    <input
                      type="text"
                      name="rendaFamiliar"
                      value={formData.rendaFamiliar}
                      onChange={handleChange}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="R$ 0,00"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-instrument font-medium text-textPrimary mb-2">
                      Observações
                    </label>
                    <textarea
                      name="observacoes"
                      value={formData.observacoes}
                      onChange={handleChange}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent font-instrument text-sm"
                      placeholder="Informações adicionais sobre o cliente"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    type="button"
                    onClick={() => setIsFormVisible(false)}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-instrument font-medium text-textPrimary hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-2 rounded-lg font-instrument font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Cadastrar Cliente
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Clients Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-fraunces font-semibold text-textPrimary">
              Clientes Cadastrados ({filteredClientes.length})
            </h2>
          </div>
          
          {/* Desktop Table - Hidden on Mobile */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    CPF
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    Contato
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    Cadastro
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-instrument font-medium text-textSecondary uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredClientes.map((cliente) => (
                  <tr key={cliente.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-instrument font-medium text-textPrimary">
                          {cliente.nome}
                        </div>
                        <div className="font-instrument text-sm text-textSecondary">
                          ID: {cliente.id.toString().padStart(3, '0')}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 font-instrument text-sm text-textPrimary">
                      {cliente.cpf}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-instrument text-sm text-textPrimary">
                        {cliente.email}  
                      </div>
                      <div className="font-instrument text-sm text-textSecondary">
                        {cliente.telefone}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-instrument font-medium rounded-full ${getStatusColor(cliente.status)}`}>
                        {cliente.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 font-instrument text-sm text-textSecondary">
                      {cliente.dataRegistro}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex justify-end space-x-2">
                        <button className="text-primary hover:text-primary/80 cursor-pointer">
                          <div className="w-8 h-8 flex items-center justify-center">
                            <i className="ri-eye-line"></i>
                          </div>
                        </button>
                        <button className="text-secondary hover:text-secondary/80 cursor-pointer">
                          <div className="w-8 h-8 flex items-center justify-center">
                            <i className="ri-edit-line"></i>
                          </div>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards - Visible only on Mobile/Tablet */}
          <div className="lg:hidden">
            <div className="divide-y divide-gray-200">
              {filteredClientes.map((cliente) => (
                <div key={cliente.id} className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h3 className="font-instrument font-medium text-textPrimary text-base">
                        {cliente.nome}
                      </h3>
                      <p className="font-instrument text-sm text-textSecondary mt-1">
                        ID: {cliente.id.toString().padStart(3, '0')}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-instrument font-medium rounded-full ${getStatusColor(cliente.status)}`}>
                        {cliente.status}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    <div className="flex items-center text-sm">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-id-card-line text-textSecondary"></i>
                      </div>
                      <span className="font-instrument text-textPrimary">{cliente.cpf}</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-mail-line text-textSecondary"></i>
                      </div>
                      <span className="font-instrument text-textPrimary">{cliente.email}</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-phone-line text-textSecondary"></i>
                      </div>
                      <span className="font-instrument text-textPrimary">{cliente.telefone}</span>
                    </div>
                    
                    <div className="flex items-center text-sm">
                      <div className="w-5 h-5 flex items-center justify-center mr-3">
                        <i className="ri-calendar-line text-textSecondary"></i>
                      </div>
                      <span className="font-instrument text-textSecondary">Cadastrado em {cliente.dataRegistro}</span>
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-3 border-t border-gray-100">
                    <button className="flex items-center text-primary hover:text-primary/80 cursor-pointer">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-eye-line"></i>
                      </div>
                      <span className="font-instrument text-sm">Visualizar</span>
                    </button>
                    <button className="flex items-center text-secondary hover:text-secondary/80 cursor-pointer">
                      <div className="w-5 h-5 flex items-center justify-center mr-2">
                        <i className="ri-edit-line"></i>
                      </div>
                      <span className="font-instrument text-sm">Editar</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Empty State */}
          {filteredClientes.length === 0 && (
            <div className="p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                <i className="ri-user-search-line text-2xl text-textSecondary"></i>
              </div>
              <h3 className="font-fraunces text-lg font-medium text-textPrimary mb-2">
                Nenhum cliente encontrado
              </h3>
              <p className="font-instrument text-textSecondary">
                {searchTerm ? 'Tente ajustar os filtros de busca' : 'Comece cadastrando seu primeiro cliente'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
